/**
 * Root web component <deepagent-agent>.
 *
 * The customer-facing API of the embed bundle. Attributes:
 *
 *   agent-id        (required)  — the DeepAgent agent UUID
 *   server          (optional)  — DeepAgent server origin
 *                                 (default: the script's serving origin)
 *   lang            (optional)  — preferred locale: en | it | es
 *   data-var-*      (optional)  — context variables (e.g. data-var-user-name)
 *   data-context    (optional)  — JSON object of context variables
 *   data-debug      (optional)  — enable verbose logging
 *
 * JS API:
 *
 *   const widget = document.querySelector('deepagent-agent');
 *   widget.setContext({ userName: 'Mario' });
 *   widget.open();   // expand panel
 *   widget.close();  // collapse to FAB
 *   await widget.startConversation({
 *     dynamicVariables: { user_name: 'Mario' }, modality: 'voice',
 *   });
 *
 * Register the exact keys under the agent's allowed dynamic variables first.
 * startConversation waits for config and rejects on unavailable agents,
 * disallowed modalities. When consent is required it opens the panel and waits
 * for acceptance; dismissing rejects with terms_not_accepted.
 * Values passed to startConversation apply only to that conversation; setContext
 * supplies persistent page context. Invoke voice starts from a visitor action.
 * Failures reject with EmbedApiError: code, missingKeys, invalidKeys,
 * unauthorizedKeys, tooLongKeys, and (for HTTP errors) status/reason.
 * setContext({ key: undefined }) removes a previously supplied persistent key.
 *
 * Hide-on-unavailable (decision D11): when the server reports the owner is
 * out of minutes (`available: false`), `render()` returns `nothing` and the
 * host element produces no visible DOM.
 */

import {
  isWebWidgetLocale,
  localizeWebWidgetTextContents,
} from "../../../shared-types/src/types/web-widget-settings.js";
import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "./deepagent-launcher.js";
import "./deepagent-nudge.js";
import "./deepagent-panel.js";
import { flyFigure } from "./figure-flight.js";

import type { AcceptTermsDetail } from "./deepagent-panel.js";

import {
  ConversationController,
  type ConversationState,
  type SessionModality,
} from "../state/conversation-controller.js";
import { NudgeController } from "../state/nudge-controller.js";
import { ContextStore } from "../state/context-store.js";
import {
  EmbedApiError,
  assertEmbedVariableRecord,
} from "../../../shared-types/src/utils/embed-dynamic-variables.js";
import { isMobileViewport } from "../utils/browser-checks.js";
import { hostPageIsDark } from "../utils/host-scheme.js";
import { previewVoiceData } from "../utils/preview-voice.js";
import type { LauncherAction } from "./deepagent-launcher.js";
import type {
  EmbedConfigResponse,
  WidgetSettingsPayload,
} from "../state/api-client.js";
import {
  getStrings,
  resolveLocale,
  selectableLocales,
  visitorLocale,
  type I18nStrings,
  type SupportedLocale,
} from "../i18n/index.js";
import { logger } from "../utils/logger.js";

/** Panel width per size step. The 380px middle value is the historic default. */
const PANEL_WIDTHS: Record<WidgetSettingsPayload["size"], number> = {
  small: 340,
  medium: 380,
  large: 420,
};

export type PreviewView = "launcher" | "panel" | "call" | "chat";

export interface StartConversationOptions {
  dynamicVariables?: Record<string, unknown>;
  modality?: SessionModality;
}

/**
 * The dark palette, as declarations rather than a rule: applied from one
 * place, the resolved `data-scheme`. Mirrors the platform's v2 dark tokens.
 */
const DARK_TOKENS = css`
  color: #ffffff;

  --da-bg:            #1c1c1c;
  --da-surface:       #262626;
  --da-surface-2:     #2e2e2e;
  --da-border:        #333333;
  --da-border-strong: #4a4a4a;
  --da-input:         #6b6b6b;

  --da-text:       #ffffff;
  --da-text-muted: #a8a8a8;
  --da-text-dim:   #6b6b6b;

  --da-online:        #3aa0ec;
  --da-danger:        #e04848;
  --da-danger-strong: #c42121;
  --da-danger-bg:     rgba(224, 72, 72, 0.14);

  --da-shadow-panel: 0 12px 32px -12px rgba(0, 0, 0, 0.6);
`;

@customElement("deepagent-agent")
export class DeepAgentAgent extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 2147483000;
      bottom: 16px;
      right: 16px;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-feature-settings: "rlig" 1, "calt" 1;
      -webkit-font-smoothing: antialiased;
      color: #383838;
      pointer-events: auto;

      /* DeepAgent Design System v2, light. */
      --da-font-display: "Manrope", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

      --da-bg:            #ffffff;
      --da-surface:       #f7f8fb;
      --da-surface-2:     #eef1f6;
      --da-border:        #e0e0e0;
      --da-border-strong: #c2c2c2;
      --da-input:         #8c8c8c;

      --da-text:       #383838;
      --da-text-muted: #6e6e6e;
      --da-text-dim:   #a3a3a3;

      /* Live and available are blue, never green. */
      --da-online:        #1183d4;
      --da-danger:        #c52020;
      --da-danger-strong: #a31b1b;
      --da-danger-bg:     rgba(197, 32, 32, 0.08);

      --da-shadow-panel: 0 12px 32px -12px rgba(0, 0, 0, 0.32);

      /* Controls, then surfaces (panel, launcher, bubbles), per the owner's corners. */
      --da-radius:    2px;
      --da-radius-lg: 2px;

      /* ── Public theming tokens ──────────────────────────────────────────────
         The owner's colour arrives from the builder; a host site can still
         override it:
           <deepagent-agent style="--da-accent:#ff6b35; --da-accent-fg:#fff">
      ─────────────────────────────────────────────────────────────────────── */
      --da-accent:      #456ce8;
      --da-accent-fg:   #ffffff;
    }

    :host([data-scheme="dark"]) { ${DARK_TOKENS} }

    :host([data-corners="soft"])  { --da-radius: 8px;   --da-radius-lg: 14px; }
    :host([data-corners="round"]) { --da-radius: 999px; --da-radius-lg: 20px; }

    :host([data-font="site"]) {
      font-family: inherit;
      --da-font-display: inherit;
    }

    /* The owner's surface: every other surface and line is mixed from it and the
       scheme's text colour, which the surface's own lightness picks. */
    :host([data-custom-bg]) {
      --da-bg:            var(--da-custom-bg);
      --da-surface:       color-mix(in srgb, var(--da-custom-bg) 95%, var(--da-text));
      --da-surface-2:     color-mix(in srgb, var(--da-custom-bg) 91%, var(--da-text));
      --da-border:        color-mix(in srgb, var(--da-custom-bg) 86%, var(--da-text));
      --da-border-strong: color-mix(in srgb, var(--da-custom-bg) 72%, var(--da-text));
      --da-input:         color-mix(in srgb, var(--da-custom-bg) 55%, var(--da-text));
    }

    :host([data-position="top-left"]) {
      top: 16px;
      left: 16px;
      right: auto;
      bottom: auto;
    }

    :host([data-position="top-right"]) {
      top: 16px;
      right: 16px;
      left: auto;
      bottom: auto;
    }

    :host([data-position="bottom-left"]) {
      bottom: 16px;
      left: 16px;
      right: auto;
      top: auto;
    }

    :host([data-position="center"]) {
      top: 50%;
      left: 50%;
      right: auto;
      bottom: auto;
      transform: translate(-50%, -50%);
    }

    /* Stacks the proactive bubble with the launcher. The bubble belongs on the
       side the widget has room on: above when anchored to the bottom, below
       when anchored to the top, and aligned to whichever edge it hugs. */
    .layer {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 10px;
    }

    :host([data-position^="top"]) .layer { flex-direction: column-reverse; }
    :host([data-position$="left"]) .layer { align-items: flex-start; }
    :host([data-position="center"]) .layer { align-items: center; }

    /* ── Mobile takeover ───────────────────────────────────────────────────
       An open panel is a full-screen sheet on a phone, so the host has to give
       up its corner: it kept a 16px offset while the panel inside asked for
       100vw by 100vh, which pushed the sheet off two edges of the screen.

       Must stay after the position rules — same specificity, so source order
       is what lets it win.
    ─────────────────────────────────────────────────────────────────────── */
    @media (max-width: 640px) {
      :host([data-open]) {
        transform: none;
        top: var(--da-vv-top, 0px);
        right: 0;
        bottom: auto;
        left: 0;
        /* The visible viewport, so the on-screen keyboard never covers the composer. */
        box-sizing: border-box;
        height: 100vh;
        height: var(--da-vv-height, 100dvh);
        /* Keeps the close button clear of the notch and the call controls
           clear of the home indicator. */
        padding:
          env(safe-area-inset-top) env(safe-area-inset-right)
          env(safe-area-inset-bottom) env(safe-area-inset-left);
      }

      :host([data-open]) .layer { height: 100%; }
    }
  `;

  @property({ type: String, attribute: "agent-id" }) agentId = "";
  @property({ type: String }) server = "";
  @property({ type: String, attribute: "lang" }) override lang = "";

  /**
   * Render from these settings instead of fetching from the server.
   *
   * Set by the agent builder so the operator previews the draft in front of
   * them — including edits that were never saved, and agents that do not exist
   * server-side yet. Re-assign it to reflect a change; the widget is otherwise
   * identical to the one visitors get, which is the point: a separate preview
   * implementation drifts, and drifted into shipping settings the real widget
   * silently ignored.
   *
   * Also gates calling: a preview has no session to start.
   */
  @property({ attribute: false }) previewConfig: EmbedConfigResponse | null =
    null;

  /**
   * Preview only: the closed launcher with its proactive bubble, the open
   * panel at rest, or the panel dressed as a running call or chat.
   */
  @property({ attribute: false }) previewView: PreviewView = "panel";

  @state() private isOpen = false;
  /** On a widget that takes both: the visitor chose to write. */
  @state() private chatChosen = false;
  @state() private locale: SupportedLocale = "en";

  /** Last accent written to the host, so we only touch the DOM on change. */
  private _appliedAccent: string | null = null;
  private _appliedSize: WidgetSettingsPayload["size"] | null = null;
  private _appliedBackground: string | null = null;
  /** True once the visitor accepted the owner's terms, for this page view. */
  @state() private termsAccepted = false;

  private controller!: ConversationController;
  private readonly context = new ContextStore();
  private configReady: Promise<void> | null = null;
  private pendingStart: {
    options: StartConversationOptions;
    strictVariables: boolean;
    resolve: () => void;
    reject: (error: Error) => void;
  } | null = null;
  /** Owns *when* the widget speaks first; this element owns what happens next. */
  private readonly nudge = new NudgeController(this);
  private strings: I18nStrings;
  /** Restored when the panel closes, so keyboard users are not dumped at the top. */
  private lastFocused: HTMLElement | null = null;
  private scrollLocked: string | null = null;
  private nudgeShownTracked = false;
  /** Pending "you forgot agent-id" diagnostic; cancelled once config arrives. */
  private configWarningTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Terms text still to be agreed to, or null when there is nothing to gate on.
   *
   * The consent gate was configurable in the builder but never rendered: an
   * owner who required consent shipped a widget that took calls without ever
   * asking. Acceptance is per page view — deliberately not persisted, since a
   * consent record belongs on the server, not in the visitor's browser.
   */
  private get pendingTerms(): string | null {
    const terms = this.controller?.widgetSettings?.terms;
    if (!terms?.enabled) return null;
    const content = terms.content?.trim();
    if (!content) return null;
    return this.termsAccepted ? null : content;
  }

  /**
   * A voice-only widget starts its one action as soon as the terms are
   * accepted. Anywhere writing is allowed, accepting only lifts the gate: the
   * composer and the microphone come alive, and the visitor decides which.
   */
  private handleAcceptTerms = (event: CustomEvent<AcceptTermsDetail>): void => {
    this.termsAccepted = true;
    const pending = this.pendingStart;
    if (pending) {
      this.pendingStart = null;
      void this.beginConversation(
        pending.options,
        pending.strictVariables,
      ).then(pending.resolve, pending.reject);
      return;
    }
    if (event.detail === "voice") this.handleStartCall();
    else if (event.detail) this.startSession(event.detail);
  };

  private handleStartCall = (): void => {
    if (this.previewConfig) {
      this.controller.showPreviewSession("voice", this.strings.previewVisitorLine);
    } else {
      this.startSession("voice");
    }
    // The pressed button leaves with the choice; focus follows the call.
    void this.updateComplete.then(() => this.focusPanel());
  };

  /** Hanging up, or ending a chat. A preview has nothing to hang up and goes back to rest. */
  private handleEndSession = (): void => {
    if (this.previewConfig) {
      this.controller.showPreviewSession(null, "");
      return;
    }
    void this.controller.end();
  };

  /**
   * The closed widget's buttons. "Call" and "Write" open the panel straight
   * into what they say; the face and label open it on the choice.
   */
  private handleLauncherAction = (event: CustomEvent<LauncherAction>): void => {
    this.openPanel("button");
    if (event.detail === "open") return;
    if (event.detail === "call") this.handleStartCall();
    else this.handleChooseChat();
  };

  private handleChooseChat = (): void => {
    // A finished or failed call keeps the panel until the visitor moves on.
    this.resetIfOver();
    this.chatChosen = true;
    void this.updateComplete.then(() => this.focusPanel());
  };

  private handleBackToChoice = (): void => {
    this.resetIfOver();
    this.chatChosen = false;
    void this.updateComplete.then(() => this.focusPanel());
  };

  private resetIfOver(): void {
    if (this.controller.state === "ended" || this.controller.state === "error")
      this.controller.reset();
  }

  /**
   * A preview has no server session behind it, so say that plainly instead of
   * attempting something that can only fail with a confusing network error.
   */
  private startSession(modality: SessionModality): void {
    void this.beginConversation({ modality }, false).catch((error: unknown) => {
      if (
        error instanceof EmbedApiError &&
        [
          "terms_not_accepted",
          "session_in_progress",
          "session_start_cancelled",
          "widget_not_connected",
        ].includes(error.code)
      )
        return;
      this.controller.errorMessage =
        error instanceof Error ? error.message : "conversation_error";
      if (
        !["active", "connecting", "requesting_mic", "unavailable"].includes(
          this.controller.state,
        )
      )
        this.controller.state = "error";
      this.requestUpdate();
    });
  }

  /**
   * A typed message. Outside a session the controller opens one and sends it on
   * connect, so the first message is the thing that starts the chat. The
   * controller clears the draft only for a message it took; a preview takes
   * none, so the operator keeps what they typed.
   */
  private handleSendMessage = (event: CustomEvent<string>): void => {
    if (this.previewConfig) {
      this.controller.errorMessage = "preview_only";
      this.requestUpdate();
      return;
    }
    this.controller.sendMessage(event.detail);
  };

  /** Typing. The composer is controlled, so every keystroke lands here. */
  private handleDraftChange = (event: CustomEvent<string>): void => {
    this.controller.draft = event.detail;
    this.requestUpdate();
  };

  /** The owner may forbid minimising; then there is no launcher to fall back to. */
  private get isCollapsible(): boolean {
    return this.controller?.widgetSettings?.collapsible !== false;
  }

  private outsideClickHandler = (event: MouseEvent) => {
    if (!this.isOpen) return;
    if (!this.isCollapsible) return;
    const path = event.composedPath();
    if (!path.includes(this)) {
      // Click outside the widget — close panel but don't end an active call.
      if (this.controller.state !== "active") {
        // The visitor clicked somewhere on the page: focus stays where they clicked.
        this.closePanel(false);
      }
    }
  };

  private lastInputWasPointer = false;

  /**
   * Escape closes the panel, except during a call.
   *
   * Hanging up on someone mid-sentence because they reached for Escape is a
   * worse outcome than the panel staying open, and it is the same rule a click
   * outside the widget already follows.
   */
  private handleKeydown = (event: KeyboardEvent): void => {
    this.lastInputWasPointer = false;
    if (event.key !== "Escape") return;
    if (!this.isOpen || !this.isCollapsible) return;
    if (this.controller.state === "active") return;
    event.stopPropagation();
    this.closePanel();
  };

  constructor() {
    super();
    this.strings = getStrings("en");
  }

  override connectedCallback(): void {
    super.connectedCallback();

    const serverUrl = this.server || this.deriveServerUrl();
    this.controller = new ConversationController(this, {
      agentId: this.agentId,
      serverUrl,
      context: this.context,
    });
    this.controller.context.hydrateFromElement(this);

    this.appliedLocale = undefined;
    this.visitorPicked = false;
    this.locale = resolveLocale(this.lang || null);
    this.strings = getStrings(this.locale);

    // Resolved from the first frame so a dark page never sees a white panel
    // flash while the config is still in flight.
    this.applyScheme();
    this.schemeObserver = new MutationObserver(this.applyScheme);
    for (const element of [document.documentElement, document.body]) {
      if (!element) continue;
      this.schemeObserver.observe(element, {
        attributes: true,
        attributeFilter: ["class", "style", "data-theme", "data-mode"],
      });
    }
    this.darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.darkQuery.addEventListener("change", this.applyScheme);
    // A page that fades its theme reports the old colour until the fade ends.
    document.addEventListener("transitionend", this.followPageFade, true);

    document.addEventListener("click", this.outsideClickHandler, {
      capture: true,
    });

    // Three config sources, decided here rather than by an early return:
    // returning before the controller exists would strand the element if the
    // preview config arrives later, which is exactly what React does — it
    // assigns the property in an effect, after mount.
    if (this.previewConfig) this.controller.applyConfig(this.previewConfig);
    else if (this.agentId) this.configReady = this.controller.loadConfig();
    else this.warnIfNeverConfigured();
  }

  /**
   * Complain about a missing `agent-id`, but only once it is actually missing.
   *
   * Neither source being present at mount is not yet a mistake: React assigns
   * `previewConfig` from an effect, which runs after the element is connected.
   * Reporting immediately meant the builder printed a red error on every visit
   * to the Web tab, for a widget that was about to be configured correctly.
   *
   * The delay is generous because this only ever helps someone debugging their
   * embed snippet, and a diagnostic that cries wolf is worse than a late one.
   */
  private warnIfNeverConfigured(): void {
    this.configWarningTimer = setTimeout(() => {
      this.configWarningTimer = null;
      if (!this.agentId && !this.previewConfig) {
        logger.error("<deepagent-agent>: missing required attribute agent-id");
      }
    }, 2_000);
  }

  override willUpdate(changed: PropertyValues<this>): void {
    // Keep the preview live: every edit in the builder lands here.
    if (changed.has("previewConfig") && this.previewConfig && this.controller) {
      if (this.configWarningTimer) {
        clearTimeout(this.configWarningTimer);
        this.configWarningTimer = null;
      }
      this.controller.applyConfig(this.previewConfig);
    }
    // Before the preview below, so its sample line speaks the labels' language.
    this.settleLocale();
    if (
      this.previewConfig &&
      this.controller &&
      (changed.has("previewConfig") || changed.has("previewView"))
    ) {
      const previous = changed.get("previewConfig") as EmbedConfigResponse | null | undefined;
      // A restyle keeps whatever the operator opened in the preview; a new view or modality starts over.
      if (
        previous &&
        !changed.has("previewView") &&
        previous.widgetSettings?.modality === this.previewConfig.widgetSettings?.modality
      ) {
        this.controller.showPreviewSession(
          this.controller.activeModality,
          this.strings.previewVisitorLine,
        );
        return;
      }
      this.isOpen = this.previewView !== "launcher";
      this.chatChosen = this.previewView === "chat";
      this.controller.showPreviewSession(
        this.previewView === "call"
          ? "voice"
          : this.previewView === "chat"
            ? "text"
            : null,
        this.strings.previewVisitorLine,
      );
    }
  }

  /**
   * A saved widget language wins. Without one the visitor's language applies;
   * when the bundle has none for them, the agent's own language, then English.
   */
  private settleLocale(): void {
    const configured = this.controller?.widgetSettings?.locale;
    if (isWebWidgetLocale(configured)) {
      if (configured === this.appliedLocale) return;
      this.appliedLocale = configured;
      this.visitorPicked = false;
      this.setLocale(configured);
      return;
    }
    // The builder preview can clear a language it had applied.
    const cleared = this.appliedLocale !== undefined;
    if (cleared) {
      this.appliedLocale = undefined;
      this.visitorPicked = false;
    }
    if (this.visitorPicked) return;
    const next = visitorLocale(
      this.lang || null,
      this.controller?.config?.languages,
    );
    if (cleared || next !== this.locale) this.setLocale(next);
  }

  private setLocale(locale: SupportedLocale): void {
    this.locale = locale;
    this.strings = getStrings(locale);
  }

  override disconnectedCallback(): void {
    this.stopPreviewTurns();
    this.followVisualViewport(false);
    this.pendingStart?.reject(new EmbedApiError("widget_not_connected"));
    this.pendingStart = null;
    this.configReady = null;
    document.removeEventListener("click", this.outsideClickHandler, {
      capture: true,
    });
    this.schemeObserver?.disconnect();
    this.schemeObserver = null;
    this.darkQuery?.removeEventListener("change", this.applyScheme);
    this.darkQuery = null;
    document.removeEventListener("transitionend", this.followPageFade, true);
    // A widget removed while its sheet was open must not leave the host page
    // unscrollable behind it.
    this.setScrollLock(false);
    if (this.configWarningTimer) {
      clearTimeout(this.configWarningTimer);
      this.configWarningTimer = null;
    }
    super.disconnectedCallback();
  }

  /** Imperative JS API — merge runtime context. */
  setContext(values: Record<string, unknown>): void {
    this.context.setContext(values);
  }

  /**
   * Start through the same consent, quota, origin and allowlist checks as the
   * widget buttons. Resolves when the SDK starts; rejects with an error code.
   * No provider credential is exposed through this API.
   */
  startConversation(options: StartConversationOptions = {}): Promise<void> {
    return this.beginConversation(options, true);
  }

  private async beginConversation(
    options: StartConversationOptions,
    strictVariables: boolean,
  ): Promise<void> {
    if (!options || typeof options !== "object" || Array.isArray(options))
      throw new EmbedApiError("invalid_request");
    if (options.dynamicVariables !== undefined)
      assertEmbedVariableRecord(options.dynamicVariables);
    // Copy before any await: callers may reuse their mutable context object.
    options = { ...options, dynamicVariables: { ...options.dynamicVariables } };
    if (!this.isConnected || !this.controller)
      throw new EmbedApiError("widget_not_connected");
    if (this.previewConfig) throw new EmbedApiError("preview_only");
    const controller = this.controller;
    await this.configReady;
    if (!this.isConnected || controller !== this.controller)
      throw new EmbedApiError("widget_not_connected");
    if (!controller.config?.available)
      throw (
        controller.lastError ??
        new EmbedApiError(
          controller.config?.unavailableReason ?? "agent_unavailable",
        )
      );
    if (
      this.pendingStart ||
      ["active", "connecting", "requesting_mic"].includes(controller.state)
    ) {
      throw new EmbedApiError("session_in_progress");
    }
    const modality = options.modality ?? "voice";
    const allowed = controller.widgetSettings?.modality ?? "voice";
    if (
      (modality !== "voice" && modality !== "text") ||
      (allowed !== "voice_text" && allowed !== modality)
    ) {
      throw new EmbedApiError("modality_not_allowed");
    }
    this.context.hydrateFromElement(this);
    if (strictVariables) {
      this.context.validatedSnapshot(
        controller.config?.requiredDynamicVars ?? [],
        options.dynamicVariables,
      );
    }
    this.openPanel("auto");
    if (this.pendingTerms) {
      return new Promise<void>((resolve, reject) => {
        this.pendingStart = { options, strictVariables, resolve, reject };
      });
    }
    const started = await controller.start(modality, {
      dynamicVariables: options.dynamicVariables,
      strictVariables,
    });
    if (!this.isConnected || controller !== this.controller)
      throw new EmbedApiError("widget_not_connected");
    if (!started)
      throw (
        controller.lastError ??
        new EmbedApiError(controller.errorMessage ?? "session_start_failed")
      );
  }

  /** Imperative JS API — open the panel. */
  open(): void {
    this.openPanel("auto");
  }

  /** Imperative JS API — close the panel. */
  close(): void {
    this.closePanel();
  }

  /**
   * The one way the panel opens.
   *
   * Three things have to happen together every time — the funnel step, the
   * proactive bubble standing down, and focus moving into the panel — and they
   * were previously spread across each caller, so a new entry point silently
   * did none of them.
   */
  private openPanel(source: "button" | "nudge" | "auto"): void {
    if (this.isOpen) return;
    this.isOpen = true;
    // A click inside the launcher retargets to this host, which cannot take focus.
    const active = document.activeElement;
    this.lastFocused =
      active instanceof HTMLElement && active !== this ? active : null;
    this.nudge.suppress("opened");
    this.controller.telemetry.track("web_widget_opened", { source });
    this.setScrollLock(isMobileViewport());
    // On a phone the sheet slides in from the edge, so the face has nowhere to fly from.
    const from =
      source === "button" && !isMobileViewport()
        ? this.renderRoot.querySelector("deepagent-launcher")?.faceRect() ?? null
        : null;
    void this.updateComplete.then(async () => {
      this.focusPanel();
      const target = from
        ? await this.renderRoot.querySelector("deepagent-panel")?.figure()
        : null;
      if (from && target)
        flyFigure({
          from,
          target,
          avatarUrl: this.controller.widgetSettings?.avatar?.url ?? null,
          layer: this.renderRoot as ShadowRoot,
          frame: this,
        });
    });
  }

  private closePanel(restoreFocus = true): void {
    this.pendingStart?.reject(new EmbedApiError("terms_not_accepted"));
    this.pendingStart = null;
    if (!this.isOpen) return;
    this.isOpen = false;
    this.setScrollLock(false);
    // Back where they came from, rather than at the top of the page.
    const previous = this.lastFocused;
    this.lastFocused = null;
    if (!restoreFocus) return;
    void this.updateComplete.then(() => {
      // A ring is for the keyboard: after a tap or a swipe it only marks a button nobody aimed at.
      const options = { focusVisible: !this.lastInputWasPointer } as FocusOptions;
      if (previous?.isConnected) previous.focus(options);
      else this.renderRoot.querySelector("deepagent-launcher")?.focus(options);
    });
  }

  private focusPanel(): void {
    this.renderRoot.querySelector("deepagent-panel")?.focusPrimary();
  }

  /**
   * Stop the page behind a full-screen sheet from scrolling.
   *
   * Only on phones: on desktop the panel is a corner card and the page beneath
   * it is still the visitor's to read. Restores the previous value rather than
   * clearing it, since the host site may run its own scroll lock.
   */
  private setScrollLock(lock: boolean): void {
    if (lock === (this.scrollLocked !== null)) return;
    if (lock) {
      this.scrollLocked = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = this.scrollLocked ?? "";
      this.scrollLocked = null;
    }
  }

  private get state(): ConversationState {
    return this.controller?.state ?? "loading_config";
  }

  private appliedLocale: SupportedLocale | undefined;
  /** The visitor chose a language in the picker: nothing but the operator overrides it. */
  private visitorPicked = false;

  /** A preview shows the bubble at once instead of waiting for its trigger. */
  private get previewNudgeVisible(): boolean {
    return (
      !!this.previewConfig &&
      this.previewView === "launcher" &&
      !!this.controller?.widgetSettings?.proactive?.enabled
    );
  }

  private get localizedContents() {
    const settings = this.controller?.widgetSettings;
    return localizeWebWidgetTextContents(
      settings?.textContents,
      this.locale,
      settings?.modality,
    );
  }

  private get effectiveStrings(): I18nStrings {
    const owner = this.localizedContents;
    if (!owner) return this.strings;
    const override = <K extends keyof I18nStrings>(
      key: K,
      value: string | undefined,
    ) => (value?.trim() ? { [key]: value } : {});
    return {
      ...this.strings,
      ...override("startCall", owner.startCall),
      ...override("endCall", owner.endCall),
      ...override("muteMicrophone", owner.muteMicrophone),
      ...override("changeLanguage", owner.changeLanguage),
      ...override("startChat", owner.startChat),
      ...override("composerPlaceholder", owner.composerPlaceholder),
      ...override("send", owner.send),
      ...override("agentTyping", owner.agentTyping),
    };
  }

  /**
   * Locales the picker may offer: the agent's configured languages narrowed to
   * what the bundle can translate. Empty (so no picker) when the owner turned
   * the selector off, or when the agent speaks a single language.
   */
  private get availableLocales(): SupportedLocale[] {
    if (!this.controller?.widgetSettings?.languageSelector) return [];
    const locales = selectableLocales(this.controller.config?.languages);
    const configuredLocale = this.controller.widgetSettings.locale;
    // The saved UI language must be an option even when conversation languages differ.
    return locales.length > 1 &&
      isWebWidgetLocale(configuredLocale) &&
      !locales.includes(configuredLocale)
      ? [configuredLocale, ...locales]
      : locales;
  }

  /** Only the page's own fade counts: every other transition on it would re-read its colours. */
  private followPageFade = (event: TransitionEvent): void => {
    if (event.target === document.documentElement || event.target === document.body)
      this.applyScheme();
  };

  private schemeObserver: MutationObserver | null = null;
  private darkQuery: MediaQueryList | null = null;

  /**
   * Light or dark, resolved onto `data-scheme` for the stylesheet. A pinned
   * theme is the owner's call; "auto" follows the page the widget sits on and
   * is re-read whenever that page switches its own theme.
   */
  private applyScheme = (): void => {
    const settings = this.controller?.widgetSettings;
    const theme = settings?.theme ?? "auto";
    const background = validHex(settings?.colors?.background);
    // A chosen surface decides the scheme: its text has to read on it.
    const scheme = background
      ? readableOn(background) === "#000000"
        ? "light"
        : "dark"
      : theme === "auto"
        ? hostPageIsDark()
          ? "dark"
          : "light"
        : theme;
    if (this.getAttribute("data-scheme") !== scheme) {
      this.setAttribute("data-scheme", scheme);
    }
  };

  /**
   * Panel width per the owner's size choice. The panel already reads
   * `--da-panel-width`; nothing was ever writing it, so every widget rendered
   * at the 380px default regardless of the setting.
   */
  private applyPanelSize(): void {
    const size = this.controller?.widgetSettings?.size;
    if (!size || this._appliedSize === size) return;
    this.style.setProperty("--da-panel-width", `${PANEL_WIDTHS[size]}px`);
    this._appliedSize = size;
  }

  /**
   * Push the owner's configured colours into the theming tokens the panel and
   * launcher already consume.
   *
   * Without this the CTA falls back to `--da-text` — white in dark mode — so an
   * operator who picked a brand colour in the builder got a white button on the
   * live widget and no way to tell why.
   *
   * Set as an inline style (not in `static styles`) because a `:host` rule
   * cannot read runtime config, and inline wins over the shadow-DOM defaults
   * while still losing to a host-site override, which is the intended order.
   */
  private applyBrandColors(): void {
    const settings = this.controller?.widgetSettings;
    const primary = settings?.colors?.primary;
    if (primary && this._appliedAccent !== primary) {
      this.style.setProperty("--da-accent", primary);
      this.style.setProperty("--da-accent-fg", readableOn(primary));
      this._appliedAccent = primary;
    }
    const background = validHex(settings?.colors?.background);
    if (this._appliedBackground !== background) {
      if (background) this.style.setProperty("--da-custom-bg", background);
      else this.style.removeProperty("--da-custom-bg");
      this.toggleAttribute("data-custom-bg", background !== null);
      this._appliedBackground = background;
    }
    const corners = settings?.corners ?? "square";
    if (this.getAttribute("data-corners") !== corners) this.setAttribute("data-corners", corners);
    const font = settings?.font ?? "default";
    if (this.getAttribute("data-font") !== font) this.setAttribute("data-font", font);
  }

  private handleLocaleChange = (event: Event): void => {
    const next = (event as CustomEvent<SupportedLocale>).detail;
    if (!isWebWidgetLocale(next) || next === this.locale) return;
    this.visitorPicked = true;
    this.setLocale(next);
  };

  override render() {
    if (!this.controller) return nothing;

    // Hide entirely when the agent is unavailable (owner out of minutes,
    // not deployed, not a WEB agent, etc.) per decision D11.
    if (this.state === "unavailable" || this.state === "loading_config") {
      // loading_config: silent during initial fetch to avoid flicker.
      return nothing;
    }

    // Reflect position attribute from widget settings.
    const position = this.controller.widgetSettings?.position;
    if (position && this.getAttribute("data-position") !== position) {
      this.setAttribute("data-position", position);
    }

    this.applyScheme();

    // Drives the mobile full-screen rule, which needs to know from CSS whether
    // the sheet is up — a state that otherwise only exists in JS.
    this.toggleAttribute("data-open", this.isOpen || !this.isCollapsible);

    this.applyBrandColors();
    this.applyPanelSize();

    return html`
      <div
        class="layer"
        @keydown=${this.handleKeydown}
        @pointerdown=${() => {
          this.lastInputWasPointer = true;
        }}
      >
        ${
          this.isOpen || !this.isCollapsible
            ? html`<deepagent-panel
              .branding=${this.controller.config?.branding ?? null}
              .avatarUrl=${this.controller.widgetSettings?.avatar?.url ?? null}
              .state=${this.controller.state}
              .isMicMuted=${this.controller.isMicMuted}
              .agentMode=${this.controller.agentMode}
              .transcript=${this.controller.transcript}
              .errorMessage=${this.controller.errorMessage}
              .strings=${this.effectiveStrings}
              .showTranscript=${this.controller.widgetSettings?.realtimeTranscript ?? true}
              .modality=${this.controller.widgetSettings?.modality ?? "voice"}
              .sessionModality=${this.controller.activeModality}
              .chatChosen=${this.chatChosen}
              .awaitingReply=${this.controller.awaitingReply}
              .draft=${this.controller.draft}
              @draft-change=${this.handleDraftChange}
              @send-message=${this.handleSendMessage}
              @choose-chat=${this.handleChooseChat}
              @back-to-choice=${this.handleBackToChoice}
              @end-chat=${this.handleEndSession}
              .showBranding=${this.controller.widgetSettings?.showBranding ?? true}
              .canClose=${this.isCollapsible}
              .termsContent=${this.pendingTerms}
              .acceptTermsLabel=${this.localizedContents.acceptTerms}
              .dismissTermsLabel=${this.localizedContents.dismissTerms}
              @accept-terms=${this.handleAcceptTerms}
              .locales=${this.availableLocales}
              .locale=${this.locale}
              @locale-change=${this.handleLocaleChange}
              .outputFreqData=${this.previewConfig ? this.previewVoice : this.outputVoice}
              .inputFreqData=${this.previewConfig ? this.previewVoice : this.inputVoice}
              @start-call=${this.handleStartCall}
              @end-call=${this.handleEndSession}
              @mute-toggle=${() => this.controller.toggleMute()}
              @close-panel=${this.handleClose}
            ></deepagent-panel>`
            : html`
              ${
                this.nudge.visible || this.previewNudgeVisible
                  ? html`<deepagent-nudge
                    .message=${this.localizedContents.proactiveMessage}
                    .dismissLabel=${this.strings.close}
                    @nudge-click=${this.handleNudgeClick}
                    @nudge-dismiss=${this.handleNudgeDismiss}
                  ></deepagent-nudge>`
                  : null
              }
              <deepagent-launcher
                .label=${this.localizedContents.mainLabel}
                .avatarUrl=${this.controller.widgetSettings?.avatar?.url ?? null}
                .modality=${this.controller.widgetSettings?.modality ?? "voice"}
                .callLabel=${this.effectiveStrings.startCall}
                .chatLabel=${this.effectiveStrings.startChat}
                .kindLabel=${this.controller.widgetSettings?.modality === "text"
                  ? this.effectiveStrings.launcherText
                  : this.effectiveStrings.launcherVoice}
                @launcher-action=${this.handleLauncherAction}
              ></deepagent-launcher>
            `
        }
      </div>
    `;
  }

  override updated(): void {
    this.syncPreviewTurns();
    this.followVisualViewport(this.hasAttribute("data-open") && isMobileViewport());
    const proactive = this.controller?.widgetSettings?.proactive;

    // Re-applied on every update because settings arrive from two directions —
    // the server once, or the builder on every keystroke — and the controller
    // only re-arms when the rule itself actually changed.
    this.nudge.configure(proactive, this.agentId || null);

    // Reported from here rather than from the controller, which owns timing and
    // has no business knowing about analytics. An edge detector, not a one-shot
    // latch: re-rendering a bubble already on screen must not count twice, but
    // a genuinely new appearance must still count.
    if (this.nudge.visible && !this.nudgeShownTracked) {
      this.nudgeShownTracked = true;
      this.controller.telemetry.track("web_widget_nudge_shown", {
        trigger: proactive?.trigger ?? "delay",
      });
    } else if (!this.nudge.visible) {
      this.nudgeShownTracked = false;
    }
  }

  private followingViewport = false;

  /**
   * A phone's keyboard shrinks the visual viewport but not the layout one, so a
   * full-screen sheet sized to the page would keep its composer under the keys.
   */
  private followVisualViewport(follow: boolean): void {
    const viewport = window.visualViewport;
    if (!viewport || follow === this.followingViewport) return;
    this.followingViewport = follow;
    if (follow) {
      viewport.addEventListener("resize", this.fitToViewport);
      viewport.addEventListener("scroll", this.fitToViewport);
      this.fitToViewport();
    } else {
      viewport.removeEventListener("resize", this.fitToViewport);
      viewport.removeEventListener("scroll", this.fitToViewport);
      this.style.removeProperty("--da-vv-height");
      this.style.removeProperty("--da-vv-top");
    }
  }

  private fitToViewport = (): void => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    this.style.setProperty("--da-vv-height", `${viewport.height}px`);
    this.style.setProperty("--da-vv-top", `${viewport.offsetTop}px`);
  };

  private previewTurnTimer: ReturnType<typeof setInterval> | null = null;

  /** A previewed call takes turns, so the operator sees the agent speak and listen. */
  private syncPreviewTurns(): void {
    const calling =
      !!this.previewConfig &&
      this.controller?.activeModality === "voice" &&
      this.controller.state === "active";
    if (calling && !this.previewTurnTimer) {
      this.previewTurnTimer = setInterval(() => {
        this.controller.agentMode =
          this.controller.agentMode === "speaking" ? "listening" : "speaking";
        this.requestUpdate();
      }, 3_200);
    } else if (!calling) {
      this.stopPreviewTurns();
    }
  }

  private stopPreviewTurns(): void {
    if (this.previewTurnTimer) clearInterval(this.previewTurnTimer);
    this.previewTurnTimer = null;
  }

  private handleNudgeClick = (): void => {
    this.nudge.suppress("clicked");
    this.openPanel("nudge");
  };

  private handleNudgeDismiss = (): void => {
    this.nudge.suppress("dismissed");
  };

  private handleClose = (): void => {
    if (this.previewConfig) {
      this.controller.showPreviewSession(null, "");
    } else if (
      ["active", "connecting", "requesting_mic"].includes(this.controller.state)
    ) {
      // A call still opening must not connect behind a closed panel.
      void this.controller.end();
    }
    this.resetIfOver();
    // A chat that never started leaves nothing to come back to.
    if (this.controller.activeModality === null) this.chatChosen = false;
    this.closePanel();
  };

  /** Stable references: a new closure per render would restart the stage's frame loop. */
  private readonly outputVoice = () => this.controller.getOutputFrequencyData();
  private readonly inputVoice = () => this.controller.getInputFrequencyData();
  private readonly previewVoice = previewVoiceData();

  private deriveServerUrl(): string {
    // The script tag that loaded us lives at /embed.js on the DeepAgent host.
    // We resolve the origin from `document.currentScript` if available, else
    // fall back to the page origin (mostly useful for the local demo).
    try {
      const scripts = Array.from(document.querySelectorAll("script[src]"));
      const script = scripts.find((s) =>
        (s as HTMLScriptElement).src.includes("/embed.js"),
      ) as HTMLScriptElement | undefined;
      if (script) {
        return new URL(script.src).origin;
      }
    } catch (error) {
      logger.warn("deriveServerUrl: fallback to location.origin", error);
    }
    return window.location.origin;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-agent": DeepAgentAgent;
  }
}

/** A `#rrggbb` colour, or null: the only form the widget mixes its surfaces from. */
function validHex(value: string | undefined): string | null {
  return value && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : null;
}

/** Black or white, whichever reads on `hex`: a yellow brand colour takes a dark label. */
function readableOn(hex: string): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return "#ffffff";
  const value = Number.parseInt(match[1] ?? "", 16);
  const channel = (shift: number) => {
    const c = ((value >> shift) & 255) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  // Where white and black give the same contrast.
  return luminance > 0.179 ? "#000000" : "#ffffff";
}
